<script setup lang="ts">
import { computed } from "vue";
import Check from "@/design/primitives/Check.vue";
import { displayOf } from "../resolvers/field-config";
import type { WidgetProps } from "./contract";

/**
 * A boolean, drawn as a switch or a checkbox.
 *
 * The difference is `config.display` and nothing else (ADR-F3): the column, the
 * value and the validation are identical, so changing your mind about the look
 * never migrates data.
 */
const props = defineProps<WidgetProps & { controlId?: string; describedBy?: string }>();
const emit = defineEmits<{ "update:modelValue": [unknown]; blur: [] }>();

const on = computed(() => props.modelValue === true);
const asCheckbox = computed(() => displayOf(props.field.config) === "checkbox");
</script>

<template>
	<!--
		Sem texto ao lado da caixa: o rótulo já está desenhado acima pelo envelope,
		e escrevê-lo de novo aqui o fazia aparecer duas vezes na mesma linha. O
		`aria-label` mantém a caixa anunciada por quem não vê o rótulo de cima.
	-->
	<Check
		v-if="asCheckbox"
		:id="controlId"
		:aria-label="field.label"
		:model-value="on"
		:disabled="disabled"
		:invalid="Boolean(error)"
		:described-by="describedBy"
		@update:model-value="emit('update:modelValue', $event)"
	/>

	<button
		v-else
		:id="controlId"
		type="button"
		role="switch"
		:aria-checked="on"
		:aria-label="field.label"
		:aria-describedby="describedBy"
		:disabled="disabled"
		class="inline-flex h-6 w-11 shrink-0 items-center rounded-full border border-border transition-colors disabled:opacity-50"
		:class="on ? 'bg-accent' : 'bg-bg-muted'"
		@click="emit('update:modelValue', !on)"
	>
		<span
			class="size-4 rounded-full bg-surface shadow transition-transform"
			:class="on ? 'translate-x-6' : 'translate-x-1'"
		/>
	</button>
</template>
